import { FC, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, IconButton, Text, VStack } from '@bonusly/design-system';
import { CheckOutline, PencilOutline, TrashOutline, XOutline } from '@bonusly/ds-icon';

import { useRichText } from '../../../components/rich-text-editor/components/hooks/use-rich-text';
import { RichTextDisplay } from '../../../components/rich-text-editor/components/rich-text-display';
import { StyledEditor } from '../../../components/rich-text-editor/components/styled-editor';
import { sanitize } from '../../../hooks/use-sanitize-rich-text';
import { EmployeeSpotlightSection } from '../../../schemas/spotlight';
import { UserSpotlightCardShell } from './user-spotlight-card-shell';
import { getUserSpotlightSectionTypeKey, getUserSpotlightSectionTypeLabelFallback } from './user-spotlight-type-label';

interface UserSpotlightCardProps {
	section: EmployeeSpotlightSection;
	isActive: boolean;
	canEdit?: boolean;
	onSave?: (textHtml: string) => void;
	onDelete?: () => void;
	onEditingChange?: (isEditing: boolean) => void;
	isSaving?: boolean;
	isDeleting?: boolean;
}

const processQuoteParagraphs = (html: string): string => {
	// Convert <p>> "..."</p> patterns to <blockquote>...</blockquote>
	return html.replace(/<p>\s*>\s*"([^"]+)"([^<]*)<\/p>/g, (match, quoteText, afterQuote) => {
		const cleanText = quoteText.trim();
		const afterText = afterQuote.trim();
		const fullText = afterText ? `${cleanText}${afterText}` : cleanText;
		return `<blockquote>${fullText}</blockquote>`;
	});
};

export const UserSpotlightCard: FC<UserSpotlightCardProps> = ({
	section,
	isActive,
	canEdit = false,
	onSave,
	onDelete,
	onEditingChange,
	isSaving = false,
	isDeleting = false,
}) => {
	const { t, i18n } = useTranslation('spotlight');
	const [isEditing, setIsEditingState] = useState(false);

	const setIsEditing = useCallback(
		(value: boolean) => {
			setIsEditingState(value);
			onEditingChange?.(value);
		},
		[onEditingChange]
	);
	const originalHtmlRef = useRef(section.textHtml);
	const fallbackLoggedKeysRef = useRef<Set<string>>(new Set());

	const { editor } = useRichText(
		{
			content: section.textHtml,
			editable: isEditing,
		},
		[isEditing]
	);

	// Check if content has changed from original
	const hasUnsavedChanges = useCallback(() => {
		if (!editor) return false;
		const currentHtml = editor.getHTML();
		return currentHtml !== originalHtmlRef.current;
	}, [editor]);

	// Handle keyboard shortcuts in editor
	const handleEditorKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			// Tab: Insert spaces instead of leaving editor
			if (event.key === 'Tab' && editor) {
				event.preventDefault();
				event.stopPropagation();
				editor.chain().focus().insertContent('    ').run();
				return;
			}

			// Escape: Exit edit mode with confirmation if unsaved changes
			if (event.key === 'Escape' && isEditing) {
				event.preventDefault();
				event.stopPropagation();

				if (hasUnsavedChanges()) {
					const shouldSave = window.confirm(t('card.confirmUnsaved'));
					if (shouldSave) {
						if (editor && onSave) {
							onSave(editor.getHTML());
						}
					} else {
						// Discard changes
						if (editor) {
							editor.commands.setContent(originalHtmlRef.current);
						}
					}
				}
				setIsEditing(false);
			}
		},
		[editor, isEditing, hasUnsavedChanges, onSave]
	);

	// Update editor content when section changes or when entering edit mode
	useLayoutEffect(() => {
		if (editor && section.textHtml) {
			editor.commands.setContent(section.textHtml);
			originalHtmlRef.current = section.textHtml;
		}
	}, [editor, section.textHtml]);

	const handleStartEdit = useCallback(() => {
		originalHtmlRef.current = section.textHtml;
		setIsEditing(true);
	}, [section.textHtml]);

	const handleSave = useCallback(() => {
		if (editor && onSave) {
			const newHtml = editor.getHTML();
			onSave(newHtml);
		}
		setIsEditing(false);
	}, [editor, onSave]);

	const handleCancel = useCallback(() => {
		if (editor) {
			editor.commands.setContent(originalHtmlRef.current);
		}
		setIsEditing(false);
	}, [editor]);

	if (!isActive) {
		return null;
	}
	const processedHtml = processQuoteParagraphs(section.textHtml);
	const sanitizedHtml = sanitize(processedHtml);
	const introText = section.intro?.replace(/\.$/, '') || '';

	return (
		<Box
			position="absolute"
			top={0}
			left={0}
			right={0}
			opacity={isActive ? 1 : 0}
			visibility={isActive ? 'visible' : 'hidden'}
			pointerEvents={isActive ? 'auto' : 'none'}
			transition="opacity 0.3s ease, visibility 0.3s ease"
		>
			<VStack spacing={6} align="stretch">
				{/* Header row with badge and edit/delete buttons */}
				<Box display="flex" alignItems="center" justifyContent="center" position="relative">
					<Box
						as="span"
						display="inline-block"
						px={4}
						py={2}
						borderRadius="full"
						fontSize="xs"
						fontWeight="semibold"
						textTransform="uppercase"
						letterSpacing="wide"
						bg="teal.100"
						color="text.default"
					>
						{(() => {
							const typeKey = getUserSpotlightSectionTypeKey(section.type);
							const translationKey = `sectionTypes.${typeKey}`;
							const hasTranslation = i18n.exists(`spotlight:${translationKey}`);

							if (!hasTranslation && !fallbackLoggedKeysRef.current.has(typeKey)) {
								fallbackLoggedKeysRef.current.add(typeKey);
								// eslint-disable-next-line no-console
								console.warn('[UserSpotlightCard] Missing translation for section type, using Title Case fallback', { type: section.type, typeKey });
							}

							return hasTranslation ? t(translationKey) : getUserSpotlightSectionTypeLabelFallback(section.type);
						})()}
					</Box>

					{/* Edit/Delete buttons - positioned above the card */}
					{canEdit && !isEditing && (
						<Box position="absolute" right={0} display="flex" gap={1}>
							<IconButton
								aria-label={t('card.editAria')}
								icon={<PencilOutline size="sm" />}
								size="sm"
								variant="ghost"
								onClick={handleStartEdit}
								color="text.soft"
								_hover={{ color: 'text.default', bg: 'whiteAlpha.500' }}
								isDisabled={isDeleting}
							/>
							<IconButton
								aria-label={t('card.deleteAria')}
								icon={<TrashOutline size="sm" />}
								size="sm"
								variant="ghost"
								onClick={onDelete}
								color="text.soft"
								_hover={{ color: 'error.text', bg: 'whiteAlpha.500' }}
								isLoading={isDeleting}
							/>
						</Box>
					)}

					{/* Edit mode action buttons - positioned above the card */}
					{isEditing && (
						<Box position="absolute" right={0} display="flex" gap={2}>
							<IconButton
								aria-label={t('card.cancelEditAria')}
								icon={<XOutline size="sm" />}
								size="sm"
								variant="ghost"
								onClick={handleCancel}
								color="error.text"
								_hover={{ bg: 'whiteAlpha.500' }}
								isDisabled={isSaving}
							/>
							<IconButton
								aria-label={t('card.saveChangesAria')}
								icon={<CheckOutline size="sm" />}
								size="sm"
								variant="ghost"
								onClick={handleSave}
								color="success.text"
								_hover={{ bg: 'whiteAlpha.500' }}
								isLoading={isSaving}
							/>
						</Box>
					)}
				</Box>

				<UserSpotlightCardShell>
					<VStack spacing={8} align="stretch">
						<VStack spacing={3} align="stretch">
							<Text
								fontFamily="'Plus Jakarta Sans', sans-serif"
								fontSize={{ base: 'md', md: 'lg' }}
								fontWeight="medium"
								lineHeight="shorter"
								color="text.default"
								opacity={0.7}
								textTransform="uppercase"
								letterSpacing="wide"
							>
								{introText}
							</Text>
							<Text
								as="h2"
								fontFamily="'Plus Jakarta Sans', sans-serif"
								fontSize={{ base: '2xl', md: '4xl' }}
								fontWeight="bold"
								lineHeight="shorter"
								color="text.default"
								mt={0}
							>
								{section.title}
							</Text>
						</VStack>

						<Box
							fontSize={{ base: 'md', md: 'xl' }}
							lineHeight="tall"
							color="text.default"
							sx={{
								'& p': {
									mb: 4,
								},
								'& blockquote': {
									position: 'relative',
									p: '16px 20px',
									m: ' 10px 0',
									borderLeft: 'none',
									borderRadius: '12px',
									bg: 'teal.50',
									fontSize: { base: 'md', md: 'xl' },
									lineHeight: 'tall',
									color: 'text.default',
									'& p': {
										mb: 0,
									},
								},
							}}
						>
							{isEditing ? (
								<Box border="1px dashed" borderColor="primary.fg" borderRadius="lg" onKeyDown={handleEditorKeyDown}>
									<StyledEditor editor={editor} />
								</Box>
							) : (
								<RichTextDisplay>{sanitizedHtml}</RichTextDisplay>
							)}
						</Box>
					</VStack>
				</UserSpotlightCardShell>
			</VStack>
		</Box>
	);
};
